import { useTranslation } from 'react-i18next'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { ProcessorDetails, useUpdateProcessor } from '@/lib/api/processors'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useToast } from '@/components/ui/use-toast'
import { ProcessorEditHeader } from '../../../components/processor-edit-header'
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit'
import { AlertTriangle } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ProcessorDryModeField } from '../../../components/processor-dry-mode-field'

interface AuthorizeNetEditPageProps {
    processor: ProcessorDetails;
}

export const AuthorizeNetProcessorEditPage = ({ processor }: AuthorizeNetEditPageProps) => {
    const { t } = useTranslation('processors')
    const navigate = useNavigate()
    const { toast } = useToast()
    const updateProcessor = useUpdateProcessor()
    const processorExit = useProcessorEditExit()

    const [validationError, setValidationError] = useState<string | null>(null)

    const [formData, setFormData] = useState({
        name: processor.name || '',
        enabled: processor.enabled ?? true,
        testMode: processor.options?.testMode ?? false,
        apiLoginId: processor.options?.apiLoginId || '',
        transactionKey: processor.options?.transactionKey || '',
        dryMode: (processor.options as any)?.dryMode === true,
    })

    const handleSubmit = async () => {
        // Validation
        if (!formData.apiLoginId.trim()) {
            setValidationError(t('apiLoginIdRequired'))
            return
        }

        if (!formData.transactionKey.trim()) {
            setValidationError(t('transactionKeyRequired'))
            return
        }

        setValidationError(null)

        try {
            await updateProcessor.mutateAsync({
                processorId: processor.id,
                updatedData: {
                    type: 'authorizenet',
                    name: formData.name,
                    enabled: formData.enabled,
                    options: {
                        testMode: formData.testMode,
                        apiLoginId: formData.apiLoginId,
                        transactionKey: formData.transactionKey,
                        dryMode: formData.dryMode,
                    }
                } as any,
            })

            toast({ description: t('processorUpdated') })
            if (processorExit.isEmbed) {
                processorExit.exitAfterSave()
                return
            }
            navigate(-1)
        } catch (error) {
            console.error('Error saving processor:', error)
            toast({ description: t('failedToSave'), variant: 'destructive' })
        }
    }

    return (
        <div className="container mx-auto py-8">
            <ProcessorEditHeader
                processorId={processor.id}
                title={t('editAuthorizeNetConfiguration')}
                onCancel={() => (processorExit.isEmbed ? processorExit.exitCancel() : navigate(-1))}
                onSave={handleSubmit}
                isSaving={updateProcessor.isPending}
            />

            {validationError && (
                <Alert variant="destructive" className="mb-6">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{validationError}</AlertDescription>
                </Alert>
            )}

            <div className="space-y-6">
                <Card>
                    <CardHeader>
                        <CardTitle>{t('generalSettings')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label htmlFor="name">{t('name')}</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder={t('enterProcessorName')}
                                required
                            />
                        </div>

                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="enabled"
                                checked={formData.enabled}
                                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked === true })}
                            />
                            <Label htmlFor="enabled">{t('enabled')}</Label>
                        </div>

                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="testMode"
                                checked={formData.testMode}
                                onCheckedChange={(checked) => setFormData({ ...formData, testMode: checked === true })}
                            />
                            <Label htmlFor="testMode">{t('enableTestMode')}</Label>
                        </div>
                        {formData.testMode && (
                            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                                <p className="text-sm text-yellow-800">
                                    {t('authorizeNetTestModeDescription')}
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle>{t('apiConfiguration')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label htmlFor="apiLoginId">{t('apiLoginId')}</Label>
                            <Input
                                id="apiLoginId"
                                value={formData.apiLoginId}
                                onChange={(e) => setFormData({ ...formData, apiLoginId: e.target.value })}
                                placeholder={t('enterApiLoginId')}
                                required
                            />
                        </div>

                        <div>
                            <Label htmlFor="transactionKey">{t('transactionKey')}</Label>
                            <Input
                                id="transactionKey"
                                type="password"
                                value={formData.transactionKey}
                                onChange={(e) => setFormData({ ...formData, transactionKey: e.target.value })}
                                placeholder={t('enterTransactionKey')}
                                required
                            />
                        </div>
                    </CardContent>
                </Card>

                <ProcessorDryModeField
                    checked={formData.dryMode}
                    onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
                    disabled={updateProcessor.isPending}
                />

            </div>
        </div>
    )
}
